// Procedural Web Audio: SFX, city ambience, exploration music and 4 synced radio stations.
import { Synthwave } from './music';
import { CITY_ART } from '../../common/src/data';
import { RainSound } from './rain-audio';
import { rainIntensity, type RainPlace } from './weather';
export const STATIONS = [
  { name: 'OFF', dj: '' },
  { name: '88.9 NEON DRIFT (synthwave)', dj: 'DJ Hikari: "It\'s always 2 a.m. somewhere, and here it\'s always 2 a.m. Keep the chrome shiny, Kabukicho."' },
  { name: '66.6 RUSTWORKS (industrial)', dj: 'MC Gearbox: "This one goes out to everyone who sold a kidney this week. Respect."' },
  { name: '93.1 RAIN ON GLASS (lo-fi)', dj: 'Nana-chan: "Rain again. Grab a Neuro-Noodle and let the drones hum you to sleep."' },
  { name: '100.0 KANEMORI VOICE (corporate)', dj: 'Kanemori Voice: "Remember: compliance is the purest form of freedom. Report suspicious joy."' },
];
const SCALES = [
  [], [0, 3, 5, 7, 10, 12, 15], [0, 1, 5, 6, 7, 11, 12], [0, 2, 4, 7, 9, 12, 14], [0, 4, 7, 12, 16, 19, 24],
];
const BPM = [0, 104, 128, 76, 96];
/** City ambience per district (city-art.json `ambience`): pad pitch (Hz) and lowpass tone, plus a filtered-noise bed. */
const AMBIENCE = CITY_ART.ambience as Record<string, { pad: number; tone: number; bedType: BiquadFilterType; bedHz: number; bed: number }>;

export class Audio {
  ctx: AudioContext | null = null;
  master!: GainNode; sfx!: GainNode; music!: GainNode;
  private engine: { osc: OscillatorNode; osc2: OscillatorNode; filt: BiquadFilterNode; gain: GainNode } | null = null;
  private siren: { osc: OscillatorNode; gain: GainNode } | null = null;
  private noiseBuf: AudioBuffer | null = null;
  volume = 0.7; radioVolume = 0.5; sfxVolume = 0.6; muted = false; radioMuted = false;
  private station = 0; private epoch = 0; private nextStep = -1; private clockOffset = 0;
  private exploring = false;
  private radioTimer: ReturnType<typeof setInterval> | null = null;
  private radioBus: GainNode | null = null;
  private soundtrack: Synthwave | null = null;
  private amb: { pad: OscillatorNode; pf: BiquadFilterNode; bf: BiquadFilterNode; bg: GainNode } | null = null;
  private district = 'kabuki';
  private rain: RainSound | null = null;

  /** Rain for where the listener is (call every frame); the weather's intensity comes from weather.ts. */
  setRain(place: RainPlace) { this.rain?.update(place, rainIntensity()); }

  /** Crossfade the city ambience to a district's profile (pad pitch and tone, noise bed). */
  setDistrict(id: string) {
    this.district = id;
    const a = this.amb, ctx = this.ctx; if (!a || !ctx) return;
    const p = AMBIENCE[id], t = ctx.currentTime; // every district has one (validateData)
    a.pad.frequency.setTargetAtTime(p.pad, t, 1.2); a.pf.frequency.setTargetAtTime(p.tone, t, 1.2);
    a.bf.type = p.bedType; a.bf.frequency.setTargetAtTime(p.bedHz, t, 0.5); a.bg.gain.setTargetAtTime(p.bed, t, 1.5);
  }

  /** iPhones suspend ("interrupted") the context on lock or a call; browsers only resume it inside a gesture */
  resume() { if (this.ctx && this.ctx.state !== 'running') void this.ctx.resume(); }
  unlock() {
    if (this.ctx) { void this.ctx.resume(); return; }
    // iPhone: Web Audio is silent with the ring switch on silent unless the page says it plays media (the title music,
    // an <audio> element, already did), so players heard music on the start page and nothing in game
    try { const s = (navigator as unknown as { audioSession?: { type: string } }).audioSession; if (s) s.type = 'playback'; } catch { /* older Safari */ }
    const ctx = new AudioContext(); this.ctx = ctx;
    // Audio time pauses when the browser suspends the context; the shared radio clock does not.
    // On resume, discard old held/queued notes so they cannot overlap the current arrangement.
    ctx.addEventListener('statechange', () => {
      if (ctx.state === 'running') this.resetRadioGraph();
      if (ctx.state === 'closed' && this.radioTimer !== null) { clearInterval(this.radioTimer); this.radioTimer = null; }
    });
    this.master = ctx.createGain(); this.master.connect(ctx.destination);
    // effects go through a limiter so gunfire and explosions don't bury the music (owner, 2026-10-12)
    const limit = ctx.createDynamicsCompressor();
    limit.threshold.value = -20; limit.knee.value = 8; limit.ratio.value = 6; limit.attack.value = 0.003; limit.release.value = 0.25;
    this.sfx = ctx.createGain(); this.sfx.connect(limit).connect(this.master);
    this.music = ctx.createGain(); this.music.connect(this.master);
    this.applyVolumes();
    const len = ctx.sampleRate; const buf = ctx.createBuffer(1, len, ctx.sampleRate); const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.noiseBuf = buf;
    // ambient city pad
    const pad = ctx.createOscillator(); pad.type = 'sawtooth'; pad.frequency.value = 55;
    const pf = ctx.createBiquadFilter(); pf.type = 'lowpass'; pf.frequency.value = 180;
    const pg = ctx.createGain(); pg.gain.value = 0.025;
    pad.connect(pf).connect(pg).connect(this.sfx); pad.start();
    this.rain = new RainSound(ctx, this.sfx, buf);
    // district bed: filtered noise under the pad (market chatter and grills in Okubo, HVAC and rail hum in Nishi)
    const bed = ctx.createBufferSource(); bed.buffer = buf; bed.loop = true;
    const bf = ctx.createBiquadFilter(); const bg = ctx.createGain(); bg.gain.value = 0;
    bed.connect(bf).connect(bg).connect(this.sfx); bed.start();
    this.amb = { pad, pf, bf, bg };
    this.setDistrict(this.district);
    // Keep the music sequencer independent of world rendering and live menu redraws.
    this.radioTimer = setInterval(() => this.tickRadio(), 50);
  }
  applyVolumes() {
    if (!this.ctx) return;
    this.master.gain.setTargetAtTime(this.muted ? 0 : this.volume, this.ctx.currentTime, 0.02);
    this.music.gain.setTargetAtTime(this.radioMuted ? 0 : this.radioVolume, this.ctx.currentTime, 0.02);
    this.sfx.gain.setTargetAtTime(this.sfxVolume, this.ctx.currentTime, 0.02);
  }
  private noise(dur: number, freq: number, type: BiquadFilterType, vol: number, at = 0) {
    const ctx = this.ctx; if (!ctx || !this.noiseBuf) return;
    const t = ctx.currentTime + at;
    const s = ctx.createBufferSource(); s.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = freq;
    const g = ctx.createGain(); g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    s.connect(f).connect(g).connect(this.sfx); s.start(t, Math.random() * 0.5); s.stop(t + dur + 0.05);
  }
  private tone(freq: number, dur: number, type: OscillatorType, vol: number, at = 0, slide = 0, dest?: AudioNode) {
    const ctx = this.ctx; if (!ctx) return;
    const t = ctx.currentTime + at;
    const o = ctx.createOscillator(); o.type = type; o.frequency.setValueAtTime(freq, t);
    if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(20, freq * slide), t + dur);
    const g = ctx.createGain(); g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g).connect(dest ?? this.sfx); o.start(t); o.stop(t + dur + 0.05);
  }
  /** Shingan: the quiet event is the wind-up tell (a rising metallic hum and a blade scrape over the 0.45 s), the loud one the sweep. */
  private naginata(vol: number, windup: boolean) {
    if (windup) {
      const v = vol * 1.8; // the wind-up arrives at half volume
      this.tone(180, 0.45, 'sawtooth', 0.07 * v, 0, 2.6); this.tone(1480, 0.45, 'triangle', 0.035 * v, 0, 1.5); this.tone(2210, 0.4, 'sine', 0.02 * v, 0.03, 1.4);
      this.noise(0.45, 3600, 'bandpass', 0.07 * v);
    } else {
      this.noise(0.28, 900, 'bandpass', 0.28 * vol); this.noise(0.2, 2800, 'highpass', 0.07 * vol); // whoosh
      this.tone(78, 0.22, 'sine', 0.24 * vol, 0.08, 0.5); this.noise(0.1, 320, 'lowpass', 0.22 * vol, 0.08); // low hit
    }
  }
  private shockwave(vol: number) {
    this.tone(46, 0.75, 'sine', 0.42 * vol, 0, 0.5); this.noise(0.6, 260, 'lowpass', 0.5 * vol); this.tone(1300, 0.3, 'sawtooth', 0.05 * vol, 0, 0.25);
    for (const d of [0.02, 0.07, 0.11, 0.19, 0.26]) this.noise(0.025, 6500, 'highpass', 0.08 * vol, d); // crackle
  }
  /** A temple bell: inharmonic partials with long decays. */
  private bell(f: number, vol: number, at = 0) {
    this.tone(f, 1.8, 'sine', 0.16 * vol, at); this.tone(f * 2.76, 1.1, 'sine', 0.07 * vol, at); this.tone(f * 5.4, 0.5, 'sine', 0.03 * vol, at);
  }
  /** Shingan's spark tags; false for any other spark. */
  private shinganSpark(vol: number, w?: string): boolean {
    switch (w) {
      case 'blink-out': this.tone(2600, 0.12, 'square', 0.05 * vol, 0, 0.12); this.noise(0.1, 6000, 'highpass', 0.08 * vol); [0.03, 0.07].forEach(d => this.tone(900 + d * 9000, 0.025, 'square', 0.04 * vol, d)); return true;
      case 'blink-in': this.noise(0.1, 6000, 'highpass', 0.04 * vol); this.tone(260, 0.12, 'square', 0.05 * vol, 0.03, 9); this.tone(90, 0.12, 'sine', 0.16 * vol, 0.1, 0.6); return true;
      case 'strip': this.tone(700, 0.55, 'sawtooth', 0.1 * vol, 0, 0.08); this.noise(0.35, 1600, 'lowpass', 0.18 * vol); this.tone(55, 0.4, 'sine', 0.15 * vol, 0.1, 0.5); return true;
      case 'arrive': this.bell(392, vol * 0.8); this.tone(196, 1.2, 'sawtooth', 0.04 * vol, 0.1, 1.5); this.tone(294, 1.2, 'triangle', 0.04 * vol, 0.1, 1.5); return true;
      case 'defeat': this.bell(330, vol * 0.8); this.tone(220, 1.6, 'sawtooth', 0.08 * vol, 0.2, 0.25); this.noise(0.9, 400, 'lowpass', 0.12 * vol, 0.2); return true;
    }
    return false;
  }
  play(kind: string, vol = 1, w?: string, quiet = false) {
    if (!this.ctx || vol <= 0.02) return;
    switch (kind) {
      case 'shot':
        if (w === 'charge') { this.tone(900, 0.25, 'sawtooth', 0.12 * vol, 0, 0.2); this.noise(0.15, 2000, 'bandpass', 0.2 * vol); }
        else if (w === 'smart') { this.tone(1400, 0.05, 'square', 0.05 * vol); this.noise(0.05, 3000, 'highpass', 0.1 * vol); }
        else if (quiet) { this.noise(0.07, 2600, 'highpass', 0.14 * vol); this.tone(240, 0.05, 'square', 0.03 * vol, 0, 0.5); } // suppressed
        else if (w === 'v47') { this.noise(0.16, 1300, 'bandpass', 0.42 * vol); this.tone(110, 0.12, 'square', 0.1 * vol, 0, 0.4); } // rifle crack
        else if (w === 'vs12') { this.noise(0.32, 500, 'lowpass', 0.6 * vol); this.tone(70, 0.24, 'sine', 0.3 * vol, 0, 0.5); } // deep blast
        else if (w === 'k5') { this.noise(0.06, 2000, 'bandpass', 0.24 * vol); this.tone(200, 0.05, 'square', 0.06 * vol, 0, 0.5); } // SMG snap
        else if (w === 'npc') { this.noise(0.09, 1200, 'bandpass', 0.18 * vol); }
        else { this.noise(0.12, 1800, 'bandpass', 0.3 * vol); this.tone(160, 0.08, 'square', 0.08 * vol, 0, 0.5); }
        break;
      case 'hit': this.noise(0.06, 700, 'lowpass', 0.2 * vol); break;
      case 'melee':
        if (w === 'naginata') { this.naginata(vol, quiet); break; }
        if (w === 'tanto') { this.noise(0.06, 4200, 'highpass', 0.16 * vol); this.tone(1900, 0.09, 'sine', 0.07 * vol, 0, 0.5); this.tone(2850, 0.06, 'triangle', 0.04 * vol, 0.02, 0.6); break; } // a fast bright neon whip
        this.noise(0.08, 400, 'lowpass', 0.3 * vol); this.tone(90, 0.1, 'sine', 0.2 * vol, 0, 0.6); break;
      case 'boom':
        if (w === 'shockwave') { this.shockwave(vol); break; }
        this.noise(0.9, 300, 'lowpass', 0.6 * vol); this.tone(60, 0.6, 'sine', 0.35 * vol, 0, 0.4); break;
      case 'crash': this.noise(0.25, 900, 'lowpass', 0.35 * vol); this.tone(110, 0.15, 'square', 0.06 * vol, 0, 0.5); break;
      case 'spark': if (!this.shinganSpark(vol, w)) this.noise(0.03, 5000, 'highpass', 0.06 * vol); break;
      case 'hack': [0, 0.05, 0.1, 0.15].forEach((d, i) => this.tone(600 + i * 250, 0.07, 'square', 0.05 * vol, d)); break;
      case 'emp': [1800, 1250, 800, 420].forEach((f, i) => this.tone(f, 0.07, 'sawtooth', 0.07 * vol, i * 0.04)); this.noise(0.22, 4500, 'highpass', 0.16 * vol); this.tone(70, 0.3, 'sine', 0.15 * vol, 0.05, 0.5); break; // power-down zap
      case 'throw': this.noise(0.05, 3000, 'highpass', 0.08 * vol); break;
      case 'smoke': this.tone(150, 0.12, 'sine', 0.18 * vol, 0, 0.5); this.noise(0.9, 1400, 'bandpass', 0.22 * vol, 0.04); break; // pop, then a long hiss
      case 'pickup': this.tone(880, 0.08, 'triangle', 0.1 * vol); this.tone(1320, 0.1, 'triangle', 0.1 * vol, 0.07); break;
      case 'decoy': // hologram: a rising glitchy chirp on spawn, a falling one as it dissolves
      if (w === 'on') { [0, 0.04, 0.08].forEach((d, i) => this.tone(500 + i * 380, 0.06, 'square', 0.05 * vol, d)); this.noise(0.1, 4000, 'highpass', 0.06 * vol); }
      else { [0, 0.05, 0.1].forEach((d, i) => this.tone(1100 - i * 380, 0.07, 'square', 0.04 * vol, d)); this.noise(0.12, 3000, 'highpass', 0.05 * vol, 0.1); }
      break;
    case 'vault': this.noise(0.15, 600, 'bandpass', 0.15 * vol); break;
      case 'ui': this.tone(1200, 0.04, 'square', 0.03 * vol); break;
      case 'alert': this.tone(440, 0.12, 'square', 0.07 * vol); this.tone(330, 0.2, 'square', 0.07 * vol, 0.12); break;
      case 'reward': [523, 659, 784, 1046].forEach((f, i) => this.tone(f, 0.15, 'triangle', 0.08 * vol, i * 0.08)); break;
    }
  }
  /** speed px/s, or -1 when not driving */
  setEngine(speed: number, type: string) {
    const ctx = this.ctx; if (!ctx) return;
    if (speed < 0) { if (this.engine) { this.engine.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.1); } return; }
    if (!this.engine) {
      const osc = ctx.createOscillator(); osc.type = 'sawtooth'; const osc2 = ctx.createOscillator(); osc2.type = 'square';
      const filt = ctx.createBiquadFilter(); filt.type = 'lowpass'; filt.frequency.value = 400;
      const gain = ctx.createGain(); gain.gain.value = 0;
      osc.connect(filt); osc2.connect(filt); filt.connect(gain).connect(this.sfx); osc.start(); osc2.start();
      this.engine = { osc, osc2, filt, gain };
    }
    const base = type === 'bike' ? 70 : type === 'armored' ? 32 : type === 'sports' ? 55 : 42;
    const f = base + speed * (type === 'bike' ? 0.6 : 0.35);
    this.engine.osc.frequency.setTargetAtTime(f, ctx.currentTime, 0.05);
    this.engine.osc2.frequency.setTargetAtTime(f * 0.5, ctx.currentTime, 0.05);
    this.engine.filt.frequency.setTargetAtTime(300 + speed * 4, ctx.currentTime, 0.05);
    this.engine.gain.gain.setTargetAtTime(0.05, ctx.currentTime, 0.1);
  }
  setSiren(vol: number) {
    const ctx = this.ctx; if (!ctx) return;
    if (!this.siren) {
      const osc = ctx.createOscillator(); osc.type = 'square'; const gain = ctx.createGain(); gain.gain.value = 0;
      const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 1500;
      osc.connect(f).connect(gain).connect(this.sfx); osc.start(); this.siren = { osc, gain };
    }
    const t = ctx.currentTime;
    this.siren.osc.frequency.setTargetAtTime(Math.floor(t * 2) % 2 ? 700 : 950, t, 0.02);
    this.siren.gain.gain.setTargetAtTime(vol * 0.035, t, 0.1);
  }
  /** Radio: all occupants compute the same step index from (serverNow - epoch), so playback is in sync. */
  setRadio(station: number, epoch: number, clockOffset: number) {
    this.selectMusic(station, epoch, clockOffset, false);
  }
  /** The mellow on-foot playlist is separate from the four original vehicle stations. */
  setExploration(clockOffset: number) {
    this.selectMusic(0, 0, clockOffset, true);
  }
  private selectMusic(station: number, epoch: number, clockOffset: number, exploring: boolean) {
    this.clockOffset = clockOffset;
    if (station !== this.station || epoch !== this.epoch || exploring !== this.exploring) {
      this.resetRadioGraph();
      this.station = station; this.epoch = epoch; this.exploring = exploring;
    }
  }
  private resetRadioGraph() {
    this.soundtrack?.dispose(); this.soundtrack = null;
    if (this.radioBus && this.ctx) {
      const old = this.radioBus, t = this.ctx.currentTime;
      old.gain.setValueAtTime(old.gain.value, t); old.gain.linearRampToValueAtTime(0, t + 0.06);
      setTimeout(() => old.disconnect(), 100);
    }
    this.radioBus = null; this.nextStep = -1;
  }
  tickRadio() {
    const ctx = this.ctx; if (!ctx || ctx.state !== 'running' || (!this.station && !this.exploring)) return;
    if (!this.radioBus) { this.radioBus = ctx.createGain(); this.radioBus.connect(this.music); }
    const serverNow = (Date.now() + this.clockOffset) / 1000;
    const pos = serverNow - this.epoch / 1000;
    if (this.exploring) {
      this.soundtrack ??= new Synthwave(ctx, this.radioBus);
      this.soundtrack.tick(pos); return;
    }
    const bpm = BPM[this.station]; const stepDur = 60 / bpm / 2; // 8th notes
    const cur = Math.floor(pos / stepDur);
    if (this.nextStep < 0 || this.nextStep < cur) this.nextStep = cur + 1;
    while (this.nextStep * stepDur < pos + 0.25) {
      const at = this.nextStep * stepDur - pos;
      if (at >= 0) this.radioStep(this.nextStep, at, stepDur);
      this.nextStep++;
    }
  }
  private radioStep(i: number, at: number, sd: number) {
    const st = this.station; const sc = SCALES[st]; const root = st === 2 ? 41 : st === 3 ? 48 : 45;
    const midi = (n: number) => 440 * Math.pow(2, (n - 69) / 12);
    const bar = Math.floor(i / 16); const chord = [0, 5, 3, 4][bar % 4];
    const h = (n: number) => ((n * 2654435761) >>> 0) / 4294967296;
    const m = this.radioBus!;
    if (st === 1) { // original Neon Drift: driving bass + arps + snare
      this.tone(midi(root - 12 + sc[chord % sc.length]), sd * 0.9, 'sawtooth', 0.18, at, 0, m);
      if (i % 2 === 0) this.tone(midi(root + 12 + sc[(i / 2 + chord) % sc.length]), sd * 1.5, 'square', 0.05, at, 0, m);
      if (i % 8 === 4) this.noiseTo(0.18, 1800, 0.12, at);
      if (i % 4 === 0) this.tone(55, 0.2, 'sine', 0.35, at, 0.4, m);
    } else if (st === 2) { // industrial: distorted kicks, metallic hits
      if (i % 2 === 0) this.tone(50, 0.18, 'square', 0.3, at, 0.3, m);
      if (h(i) > 0.6) this.noiseTo(0.07, 5000, 0.1, at);
      if (i % 16 === 8) this.tone(midi(root + sc[Math.floor(h(bar) * sc.length)]), sd * 4, 'sawtooth', 0.08, at, 0.5, m);
    } else if (st === 3) { // lo-fi: soft chords, dusty hats
      if (i % 8 === 0) for (const k of [0, 2, 4]) this.tone(midi(root + sc[(chord + k) % sc.length]), sd * 7, 'triangle', 0.06, at, 0, m);
      if (i % 2 === 1) this.noiseTo(0.03, 7000, 0.04, at);
      if (i % 8 === 0 || i % 8 === 5) this.tone(60, 0.25, 'sine', 0.25, at, 0.5, m);
    } else if (st === 4) { // corporate: cheerful jingle loop
      const mel = [0, 2, 4, 2, 5, 4, 2, 0];
      if (i % 2 === 0) this.tone(midi(root + 12 + sc[mel[(i / 2) % 8] % sc.length]), sd * 1.6, 'triangle', 0.08, at, 0, m);
      if (i % 4 === 0) this.tone(midi(root - 12 + sc[chord % sc.length]), sd * 3, 'sine', 0.12, at, 0, m);
      if (i % 32 === 0) [0, 4, 7].forEach((n, k) => this.tone(midi(root + 24 + n), 0.15, 'sine', 0.05, at + k * 0.08, 0, m));
    }
  }
  private noiseTo(dur: number, freq: number, vol: number, at: number) {
    const ctx = this.ctx; if (!ctx || !this.noiseBuf) return;
    const t = ctx.currentTime + at;
    const s = ctx.createBufferSource(); s.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter(); f.type = 'highpass'; f.frequency.value = freq;
    const g = ctx.createGain(); g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    s.connect(f).connect(g).connect(this.radioBus!); s.start(t, Math.random() * 0.5); s.stop(t + dur + 0.05);
  }
}
